import { useEffect, useState } from 'react'

let isGoogleMapsLoaded = false
let isGoogleMapsLoading = false
const callbacks: (() => void)[] = []

export function loadGoogleMapsScript(): Promise<void> {
    if (isGoogleMapsLoaded) {
        return Promise.resolve()
    }

    if (isGoogleMapsLoading) {
        return new Promise<void>((resolve) => {
            callbacks.push(() => resolve())
        })
    }

    isGoogleMapsLoading = true

    return new Promise<void>((resolve, reject) => {
        window.initGoogleMaps = () => {
            isGoogleMapsLoaded = true
            isGoogleMapsLoading = false

            callbacks.forEach((callback) => callback())
            callbacks.length = 0

            resolve()
        }

        const script = document.createElement('script')
        script.id = 'google-maps-script'
        script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.NEXT_PUBLIC_GOOGLE_PLACES_API_KEY}&libraries=places&callback=initGoogleMaps`
        script.async = true
        script.defer = true

        script.onerror = (error) => {
            isGoogleMapsLoading = false
            reject(error)
        }

        document.head.appendChild(script)
    })
}

export function useGoogleMapsLoaded(enabled = true) {
    const [isLoaded, setIsLoaded] = useState(isGoogleMapsLoaded)

    useEffect(() => {
        if (!enabled) return

        if (isGoogleMapsLoaded) {
            setIsLoaded(true)
        } else {
            loadGoogleMapsScript().then(() => {
                setIsLoaded(true)
            })
        }
    }, [enabled])

    return isLoaded
}

// Reverse-geocodes client-side via the Google Maps JS SDK — the signed-out counterpart
// to Nile-Core's own (BAA-covered, authenticated) reverse-geocode endpoint, which a
// signed-out visitor can't call. Awaits the Maps script directly (resolves immediately
// if already loaded) rather than assuming a caller's own useGoogleMapsLoaded has
// finished first.
export async function reverseGeocodeToPostalCode(coordinates: {
    latitude: number
    longitude: number
}): Promise<string | undefined> {
    await loadGoogleMapsScript()
    return new Promise((resolve) => {
        new google.maps.Geocoder().geocode(
            { location: { lat: coordinates.latitude, lng: coordinates.longitude } },
            (results, status) => {
                const postalComponent =
                    status === 'OK'
                        ? results?.[0]?.address_components?.find((component) => component.types?.includes('postal_code'))
                        : undefined
                resolve(postalComponent?.long_name)
            },
        )
    })
}

export function GoogleMapsProvider({ children }: { children: React.ReactNode }) {
    const isLoaded = useGoogleMapsLoaded()

    if (!isLoaded) {
        return <div>Loading Maps...</div>
    }

    return <>{children}</>
}
